import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { lineParent, lineChild } from "./motion";
import { useLang } from "@/i18n/LanguageContext";

const EARTH_VIDEO = "https://videos.pexels.com/video-files/33474466/14240764_2560_1440_24fps.mp4";

export const Hero = () => {
  const { t, dir } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const lines = [t.hero.l1, t.hero.l2, t.hero.l3, t.hero.l4];

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-white pt-20">
      {/* Parallax clipped earth video, asymmetric right (night from space) */}
      <motion.div
        style={{ y: imgY }}
        className={`pointer-events-none absolute ${dir === "rtl" ? "left-[4vw]" : "right-[4vw]"} top-[18%] hidden lg:block w-[40vw] max-w-[640px] h-[64vh] overflow-hidden bg-black`}
      >
        <video
          src={EARTH_VIDEO}
          poster="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1200&auto=format&fit=crop"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-vw-green/25 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
        <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-12 flex flex-col justify-center min-h-[calc(100svh-5rem)]"
      >
        <div className="w-full lg:w-[52%] lg:pr-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="vw-eyebrow text-vw-green mb-8 text-sm font-medium tracking-[0.18em]"
          >
            {t.hero.eyebrow}
          </motion.div>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-serif text-vw-ink tracking-tighter leading-[0.98] text-5xl md:text-6xl lg:text-[5.2rem] max-w-[15ch]"
          >
            {lines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  variants={lineChild}
                  className={`block ${i === 1 || i === 3 ? "italic font-light text-vw-green" : "font-medium"}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-vw-ink2 leading-relaxed"
          >
            {t.hero.para}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a href="#products" data-testid="hero-explore-btn" className="btn-sweep bg-vw-green text-white px-8 py-4 text-sm font-medium hover:text-white transition-colors duration-500">
              {t.hero.explore}
            </a>
            <a href="#contact" data-testid="hero-contact-btn" className="px-8 py-4 text-sm font-medium border border-vw-green text-vw-green hover:bg-vw-bg3 transition-colors duration-500">
              {t.hero.enquiry}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex items-center gap-3 font-mono uppercase tracking-[0.2em] text-[0.85rem] text-vw-ink2"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
        {t.hero.scroll}
      </motion.div>
    </section>
  );
};
